<script setup lang="ts" generic="V extends string | number | boolean">
// ================================
// LES PUCES DE CHOIX du formulaire (série UX du témoin, 15/10/2026, 2.2).
//
// Ce que le courtier doit vivre : le choix courant se voit d'un coup d'œil,
// en bleu plein avec une coche, comme le contrôle d'objectif ; il ne
// ressemble plus à un bouton désactivé (gris plein). Un seul composant pour
// le segment, la marge, le scénario, le branchement et les paliers.
//
// Accessibilité : un groupe `radiogroup`, chaque puce un `radio` avec
// `aria-checked` ; les flèches déplacent le choix, seule la puce choisie
// (ou la première) entre dans l'ordre de tabulation.
// ================================
import { ref } from "vue"

export type Puce<V> = {
    valeur: V
    libelle: string
    /** L'identifiant de test de la puce. */
    testid?: string
}

const props = defineProps<{
    modelValue: V | null | undefined
    puces: Puce<V>[]
    /** Le nom du groupe, lu par un lecteur d'écran. */
    libelle: string
    testid?: string
}>()

const emit = defineEmits<{ "update:modelValue": [valeur: V] }>()

const boutons = ref<HTMLButtonElement[]>([])

function estChoisie(puce: Puce<V>): boolean {
    return puce.valeur === props.modelValue
}

function entreDansLaTabulation(index: number): boolean {
    const choisie = props.puces.findIndex(estChoisie)
    return choisie === -1 ? index === 0 : index === choisie
}

function choisir(index: number): void {
    const puce = props.puces[index]
    if (puce) emit("update:modelValue", puce.valeur)
}

function deplacer(index: number, pas: number): void {
    const n = props.puces.length
    if (n === 0) return
    const suivant = (index + pas + n) % n
    choisir(suivant)
    boutons.value[suivant]?.focus()
}
</script>

<template>
    <div
        role="radiogroup"
        :aria-label="libelle"
        class="flex flex-wrap gap-2"
        :data-testid="testid"
    >
        <button
            v-for="(puce, index) in puces"
            :key="String(puce.valeur)"
            ref="boutons"
            type="button"
            role="radio"
            :aria-checked="estChoisie(puce)"
            :tabindex="entreDansLaTabulation(index) ? 0 : -1"
            class="inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-400"
            :class="
                estChoisie(puce)
                    ? 'border-primary bg-primary font-medium text-white'
                    : 'border-surface-300 bg-white text-primary-700 hover:bg-surface-100'
            "
            :data-testid="puce.testid"
            @click="choisir(index)"
            @keydown.right.prevent="deplacer(index, 1)"
            @keydown.down.prevent="deplacer(index, 1)"
            @keydown.left.prevent="deplacer(index, -1)"
            @keydown.up.prevent="deplacer(index, -1)"
        >
            <i
                v-if="estChoisie(puce)"
                class="pi pi-check text-xs"
                aria-hidden="true"
            />
            {{ puce.libelle }}
        </button>
    </div>
</template>
